import { useLayoutEffect, useMemo, useState } from 'react';
import cx from 'clsx';
import equal from 'fast-deep-equal';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import type { TableColumns } from '@ff-it/ui';
import type { ControlledTableColumnConfig } from './useColumnConfig';
import type { RowType } from './types';
import * as styles from './ControlledTable.css';

/**
 * Given `ControlledTable` columns with configuration, return `TableColumn`s with additional props so that:
 *
 * - hidden columns are excluded;
 * - pinned columns are ordered to the table's left/right edges;
 * - pinned columns get the CSS classes and sticky offset styles that make them pin - with the offsets measured from the
 *   live header-cell widths and kept in sync as the table (or its columns) resize.
 *
 * @param columns     The configured ControlledTable columns.
 * @param container   The table wrapper DOM element, used to measure the header cells and observe resizes.
 */
export function useColumnLayout<R extends RowType>(
  columns: ControlledTableColumnConfig<R>[],
  container: HTMLElement | null,
): TableColumns<R> {
  const arranged = useMemo(() => arrangeVisibleColumns(columns), [columns]);
  const offsets = useMeasuredOffsets(arranged, container);
  return useMemo(() => applyPinStyles(arranged, offsets), [arranged, offsets]);
}

/**
 * Return the visible columns arranged in display order, based on visibility and pinning configuration.
 */
function arrangeVisibleColumns<R>(columns: ControlledTableColumnConfig<R>[]): ControlledTableColumnConfig<R>[] {
  const visible = columns.filter((c) => c.visible);
  const lefts = visible.filter((c) => c.pinned === 'left');
  const rights = visible.filter((c) => c.pinned === 'right');
  return [...lefts, ...visible.filter((c) => !c.pinned), ...rights];
}

/**
 * Return up-to-date offset values for pinned columns as mapping from the index of the pinned column in `arranged` to
 * the offset value.
 */
function useMeasuredOffsets<R>(
  arranged: ControlledTableColumnConfig<R>[],
  container: HTMLElement | null,
): Record<number, number> {
  const [offsets, setOffsets] = useState<Record<number, number>>({});
  useLayoutEffect(() => {
    if (!container || !arranged.some((c) => c.pinned)) {
      return;
    }
    const headCells = (): HTMLElement[] => {
      const headRow = container.querySelector(':scope > table > thead > tr');
      return headRow ? (Array.from(headRow.children) as HTMLElement[]) : [];
    };
    const width = (cell: HTMLElement | undefined): number => cell?.getBoundingClientRect().width ?? 0;
    const measure = (): void => {
      const cells = headCells();
      const next: Record<number, number> = {};
      // left offsets accumulate front-to-back, right offsets back-to-front (each from its own edge)
      let l = 0;
      for (let i = 0; i < arranged.length; i++) {
        if (arranged[i].pinned === 'left') {
          next[i] = l;
          l += width(cells[i]);
        }
      }
      let r = 0;
      for (let i = arranged.length - 1; i >= 0; i--) {
        if (arranged[i].pinned === 'right') {
          next[i] = r;
          r += width(cells[i]);
        }
      }
      setOffsets((prev) => (equal(prev, next) ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    const cells = headCells();
    arranged.forEach((c, i) => {
      if (c.pinned && cells[i]) {
        ro.observe(cells[i]);
      }
    });
    return () => ro.disconnect();
  }, [container, arranged]);
  return offsets;
}

function applyPinStyles<R>(
  columns: ControlledTableColumnConfig<R>[],
  offsets: Record<number, number>,
): TableColumns<R> {
  return columns.map((c, i) => {
    if (!c.pinned) {
      return c.column;
    }
    const isLeft = c.pinned === 'left';
    const isBoundaryLeft = isLeft && columns[i + 1]?.pinned !== 'left';
    const isBoundaryRight = !isLeft && columns[i - 1]?.pinned !== 'right';
    const offsetStyle = assignInlineVars({
      [styles.pinOffset]: `${offsets[i] ?? 0}px`,
    });
    const { cellProps, headerCellProps } = c.column;
    return {
      ...c.column,
      cellProps: {
        ...cellProps,
        className: cx(
          cellProps?.className,
          isLeft ? styles.pinnedLeftBody : styles.pinnedRightBody,
          isBoundaryLeft && styles.pinBoundaryLeft,
          isBoundaryRight && styles.pinBoundaryRight,
        ),
        style: { ...cellProps?.style, ...offsetStyle },
      },
      headerCellProps: {
        ...headerCellProps,
        className: cx(
          headerCellProps?.className || cellProps?.className,
          isLeft ? styles.pinnedLeftHeader : styles.pinnedRightHeader,
          isBoundaryLeft && styles.pinBoundaryLeft,
          isBoundaryRight && styles.pinBoundaryRight,
        ),
        style: { ...headerCellProps?.style, ...offsetStyle },
      },
    };
  });
}
